import type { Medication, MedRequest } from '@/types/data'
import { jsonSafeParse } from '@/utils/jsonSafeParse'

// `orderedItem` is the prescribed (current) variation, not the originally ordered one.
export const findCatalogueMedication = (order: MedRequest, medications: Medication[] | undefined) =>
  medications?.find(
    medication => medication.variantHash === order.orderedItem.id || medication.productHash === order.orderedItem.id
  )

export const getMedicationName = (order: MedRequest, medication: Medication | undefined) =>
  order.prescribedItemName ||
  medication?.productName ||
  [order.orderedItem.name, order.orderedItem.strength].filter(Boolean).join(' ') ||
  'Unknown medication'

/** The order's directions when they differ from the catalogue's, otherwise null. */
export const getChangedDirections = (order: MedRequest, medication: Medication | undefined) =>
  typeof medication?.medicationDirections === 'string' &&
  typeof order.medicationDirections === 'string' &&
  medication.medicationDirections !== order.medicationDirections
    ? order.medicationDirections
    : null

type ConsultationAnswer = { type?: string; title?: unknown; answer?: unknown } | null

/**
 * Files the patient uploaded in the order's consultation. Skips parseQuestionnaire, which Sentry-reports
 * malformed JSON on every call. `imageUrls` are the 'uploader' arrays NodeAPI presigns; `workDriveLinks`
 * are legacy single-string answers, password-protected WorkDrive pages that can't render as images.
 */
export function getConsultationUploads(order: MedRequest) {
  const questions = jsonSafeParse<Record<string, ConsultationAnswer>>(order.questionaire)
  const imageUrls: string[] = []
  const workDriveLinks: string[] = []

  for (const question of Object.values(questions ?? {})) {
    const { type, title, answer } = question ?? {}
    const isUpload = type === 'uploader' || (typeof title === 'string' && title.includes('Provide picture'))

    if (type === 'uploader' && Array.isArray(answer)) {
      imageUrls.push(...answer.filter((link): link is string => typeof link === 'string' && link.startsWith('http')))
    } else if (isUpload && typeof answer === 'string' && answer) {
      // The same link parseQuestionnaire builds for the consultation table.
      workDriveLinks.push(`https://workdrive.zohoexternal.com/external/${answer}`)
    }
  }

  return { imageUrls, workDriveLinks }
}
